import { Box, CircularProgress, Typography } from "@mui/material";
import MicOffIcon from "@mui/icons-material/MicOff";
import type { JSX, RefObject } from "react";

import { AudioMeter } from "../AudioMeter.js";

/**
 * The in-call video stage, extracted from `CallWindow.tsx`: the You/peer mic
 * meters, the primary video (peer screen > peer camera > audio-only
 * placeholder), the self-camera PiP, and the "you're sharing" self-screen
 * preview. Presentational — the `<video>` refs are owned by `CallWindow` (it
 * binds the streams in effects), so this component just renders them.
 */
export function VideoStage({
  maximized,
  peerName,
  muted,
  localAudioStream,
  remoteStream,
  hasScreenShare,
  hasPeerCamera,
  peerVideoConnecting,
  localCameraStream,
  localScreenStream,
  pipW,
  pipH,
  remoteScreenRef,
  remoteCameraRef,
  localCameraRef,
  localScreenRef,
}: {
  maximized: boolean;
  peerName: string | undefined;
  muted: boolean;
  localAudioStream: MediaStream | null;
  remoteStream: MediaStream | null;
  hasScreenShare: boolean;
  hasPeerCamera: boolean;
  peerVideoConnecting: boolean;
  localCameraStream: MediaStream | null;
  localScreenStream: MediaStream | null;
  pipW: number;
  pipH: number;
  remoteScreenRef: RefObject<HTMLVideoElement | null>;
  remoteCameraRef: RefObject<HTMLVideoElement | null>;
  localCameraRef: RefObject<HTMLVideoElement | null>;
  localScreenRef: RefObject<HTMLVideoElement | null>;
}): JSX.Element {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(maximized ? { flex: 1, minHeight: 0 } : { aspectRatio: "16 / 9" }),
      }}
    >
      {/* Mic-activity meters (Google-Meet style), top-left — so you can
          always see your OWN mic AND the peer's mic are live + speaking. */}
      <Box
        data-testid="call-audio-meters"
        sx={{ position: "absolute", top: 8, left: 8, zIndex: 2, display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, bgcolor: "rgba(0,0,0,0.55)", borderRadius: 1, px: 0.75, height: 22 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 600 }}>You</Typography>
          {muted ? (
            <MicOffIcon data-testid="self-muted-icon" sx={{ fontSize: 13, color: "#ff5252" }} />
          ) : (
            <AudioMeter stream={localAudioStream} height={12} />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, bgcolor: "rgba(0,0,0,0.55)", borderRadius: 1, px: 0.75, height: 22 }}>
          <Typography
            noWrap
            sx={{ color: "rgba(255,255,255,0.85)", fontSize: 10, fontWeight: 600, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {peerName ?? "Peer"}
          </Typography>
          <AudioMeter stream={remoteStream} color="#7aa2ff" height={12} />
        </Box>
      </Box>

      {/* Primary: screen share > peer camera > audio-only placeholder */}
      {hasScreenShare ? (
        <>
          <video
            ref={remoteScreenRef}
            data-testid="remote-screen-video"
            autoPlay
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
          {hasPeerCamera && (
            <video
              ref={remoteCameraRef}
              data-testid="remote-camera-video"
              autoPlay
              playsInline
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 96,
                height: 54,
                objectFit: "cover",
                borderRadius: 4,
                border: "1px solid rgba(255,255,255,0.35)",
              }}
            />
          )}
        </>
      ) : hasPeerCamera ? (
        <video
          ref={remoteCameraRef}
          data-testid="remote-camera-video"
          autoPlay
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : peerVideoConnecting ? (
        <Box
          data-testid="peer-video-connecting"
          sx={{ textAlign: "center", px: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
        >
          <CircularProgress size={28} thickness={4} sx={{ color: "rgba(255,255,255,0.85)" }} />
          <Typography sx={{ color: "common.white", fontSize: 14 }}>
            Connecting {peerName ?? "peer"}'s video…
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", px: 2 }}>
          <Typography sx={{ color: "common.white", fontSize: 15, fontWeight: 600 }}>
            {peerName ?? "On a call"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: 0.5 }}>
            Audio call — turn on your camera to share video
          </Typography>
        </Box>
      )}

      {/* Self-view (my camera) PiP, bottom-right. Gated on the STREAM
          (not cameraOn) so the <video> mounts the same render the stream
          binds — fixes the black self-preview. */}
      {localCameraStream && (
        <video
          ref={localCameraRef}
          data-testid="local-camera-video"
          autoPlay
          playsInline
          muted
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            width: pipW,
            height: pipH,
            objectFit: "cover",
            borderRadius: 4,
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        />
      )}

      {/* Self-preview: MY screen share, bottom-left — so the sharer sees
          what they're sharing (and the peer's screen stays the primary,
          never the sharer's own — that was the "I see my own screen" bug). */}
      {localScreenStream && (
        <Box sx={{ position: "absolute", bottom: 8, left: 8 }}>
          <video
            ref={localScreenRef}
            data-testid="local-screen-video"
            autoPlay
            playsInline
            muted
            style={{
              width: pipW,
              height: pipH,
              objectFit: "contain",
              background: "#000",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.35)",
              display: "block",
            }}
          />
          <Typography
            sx={{
              position: "absolute",
              top: 2,
              left: 4,
              color: "rgba(255,255,255,0.8)",
              fontSize: 9,
              fontWeight: 600,
              textShadow: "0 1px 2px rgba(0,0,0,0.8)",
            }}
          >
            You're sharing
          </Typography>
        </Box>
      )}
    </Box>
  );
}
